/**
 * VehicleInsuranceTab - Enhanced insurance tab with État de Parc data
 *
 * Displays:
 * - État de Parc (vertical card format)
 * - Insurance contracts
 * - Claims history
 *
 * ALL ACCORDIONS CLOSED BY DEFAULT
 */
import React from 'react';
import { useOrgCurrency } from '../../../hooks/useOrgCurrency.js';
import { useFormat, formatDate as fmtDate, formatNumber as fmtNumber } from '../../../i18n/format.js';
import {
  Shield, AlertTriangle, CheckCircle, Clock, FileText,
  Car, MapPin
} from 'lucide-react';
import {
  useVehicleInsuranceData,
  ETAT_DE_PARC_FIELDS,
  CLAIM_TYPE_LABELS,
  CLAIM_STATUS_LABELS
} from '../../../hooks/useVehicleInsuranceData.js';
import { useAccordionState } from '../../../hooks/useAccordionState.js';
import { AccordionGroup, AccordionContainer } from '../../common/AccordionGroup.jsx';
import { EmptyState } from '../../common/EntityDetailModal.jsx';

// Format field values based on type
function formatFieldValue(field, value, money) {
  if (value === null || value === undefined) return 'N/A';

  switch (field.format) {
    case 'date':
      return value ? fmtDate(value) : 'N/A';
    case 'currency':
      return value ? money(value) : 'N/A';
    case 'km':
      return value ? `${fmtNumber(parseInt(value))} km` : 'N/A';
    case 'boolean':
      return value ? '✓ Inclus' : '✗ Non inclus';
    default:
      return value || 'N/A';
  }
}

// État de Parc vertical card
function EtatDeParcCard({ data, money }) {
  if (!data) return null;

  return (
    <div className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-100">
      {ETAT_DE_PARC_FIELDS.map(field => {
        const value = data[field.key];
        const formattedValue = formatFieldValue(field, value, money);
        const isBoolean = field.format === 'boolean';

        return (
          <div
            key={field.key}
            className="flex justify-between items-center py-3 px-4 hover:bg-gray-50"
          >
            <span className="text-sm text-gray-600 font-medium">{field.label}</span>
            <span className={`text-sm font-semibold ${
              isBoolean ? (value ? 'text-green-600' : 'text-gray-400') : 'text-gray-900'
            }`}>
              {formattedValue}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// Contract card
function ContractCard({ vi, onMessage, money }) {
  const { fmt } = useFormat();
  const { contract, isPrimary, daysUntilExpiry } = vi;
  if (!contract) return null;

  const isExpired = daysUntilExpiry !== null && daysUntilExpiry < 0;
  const isExpiringSoon = daysUntilExpiry !== null && daysUntilExpiry >= 0 && daysUntilExpiry <= 60;

  return (
    <div className={`border rounded-lg p-4 ${
      isPrimary ? 'border-blue-300 bg-blue-50' : 'border-gray-200 bg-white'
    }`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <Shield className={`w-5 h-5 ${isPrimary ? 'text-blue-600' : 'text-gray-500'}`} />
          <div>
            <p className="font-semibold text-gray-900">
              {contract.product_name || contract.insurer_name}
            </p>
            <p className="text-sm text-gray-600">{contract.policy_number}</p>
          </div>
        </div>
        {isPrimary && (
          <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
            Principal
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3 text-sm">
        <div>
          <p className="text-gray-500">Assureur</p>
          <p className="font-medium text-gray-900">{contract.insurer_name}</p>
        </div>
        <div>
          <p className="text-gray-500">Prime véhicule</p>
          <p className="font-medium text-gray-900">
            {vi.premium_amount != null ? money(vi.premium_amount) : (contract.total_premium_ttc ? `${money(contract.total_premium_ttc)} (contrat)` : 'N/A')}
          </p>
        </div>
        <div>
          <p className="text-gray-500">Franchise</p>
          <p className="font-medium text-gray-900">
            {vi.deductible_amount != null ? money(vi.deductible_amount) : (contract.deductible ? `${money(contract.deductible)} (contrat)` : 'N/A')}
          </p>
        </div>
        <div>
          <p className="text-gray-500">Modèle couverture</p>
          <p className="font-medium text-gray-900">
            {vi.coverage_model?.name || 'N/A'}
          </p>
        </div>
        <div>
          <p className="text-gray-500">Date d'effet</p>
          <p className="font-medium text-gray-900">
            {contract.effective_date ? fmt.date(contract.effective_date) : 'N/A'}
          </p>
        </div>
        <div>
          <p className="text-gray-500">Date d'expiration</p>
          <p className={`font-medium ${
            isExpired ? 'text-red-600' : isExpiringSoon ? 'text-yellow-600' : 'text-gray-900'
          }`}>
            {contract.expiry_date ? fmt.date(contract.expiry_date) : 'N/A'}
          </p>
        </div>
      </div>

      {/* Expiry status */}
      {daysUntilExpiry !== null && (
        <div className={`mt-3 pt-3 border-t ${
          isExpired ? 'border-red-200' : isExpiringSoon ? 'border-yellow-200' : 'border-gray-200'
        }`}>
          {isExpired ? (
            <div className="flex items-center gap-2 text-red-600 text-sm">
              <AlertTriangle className="w-4 h-4" />
              <span className="font-semibold">Expiré depuis {Math.abs(daysUntilExpiry)} jour(s)</span>
            </div>
          ) : isExpiringSoon ? (
            <div className="flex items-center gap-2 text-yellow-600 text-sm">
              <Clock className="w-4 h-4" />
              <span className="font-semibold">Expire dans {daysUntilExpiry} jour(s)</span>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-green-600 text-sm">
              <CheckCircle className="w-4 h-4" />
              <span className="font-semibold">Valide ({daysUntilExpiry} jours restants)</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Claim card
function ClaimCard({ claim, money }) {
  const { fmt } = useFormat();
  const statusColors = {
    draft: 'bg-gray-100 text-gray-800',
    declared: 'bg-blue-100 text-blue-800',
    acknowledged: 'bg-purple-100 text-purple-800',
    expert_pending: 'bg-yellow-100 text-yellow-800',
    expert_done: 'bg-indigo-100 text-indigo-800',
    repair_authorized: 'bg-cyan-100 text-cyan-800',
    indemnified: 'bg-green-100 text-green-800',
    closed: 'bg-gray-100 text-gray-800',
    rejected: 'bg-red-100 text-red-800'
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-white">
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-red-500" />
          <div>
            <p className="font-semibold text-gray-900">
              {CLAIM_TYPE_LABELS[claim.type] || claim.type}
            </p>
            <p className="text-sm text-gray-500">
              {claim.event_date ? fmt.date(claim.event_date) : 'N/A'}
            </p>
          </div>
        </div>
        <span className={`px-2 py-1 text-xs rounded-full ${statusColors[claim.status] || statusColors.draft}`}>
          {CLAIM_STATUS_LABELS[claim.status] || claim.status}
        </span>
      </div>

      {claim.description && (
        <p className="text-sm text-gray-600 mt-2 line-clamp-2">{claim.description}</p>
      )}

      <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t border-gray-100 text-sm">
        <div>
          <p className="text-gray-500">Montant estimé</p>
          <p className="font-medium text-red-600">
            {claim.estimated_amount ? money(claim.estimated_amount) : 'N/A'}
          </p>
        </div>
        <div>
          <p className="text-gray-500">Indemnisé</p>
          <p className="font-medium text-green-600">
            {claim.indemnity_amount ? money(claim.indemnity_amount) : '-'}
          </p>
        </div>
      </div>

      {claim.location && (
        <div className="flex items-center gap-1 mt-2 text-xs text-gray-500">
          <MapPin className="w-3 h-3" />
          {claim.location}
        </div>
      )}
    </div>
  );
}

export function VehicleInsuranceTab({ entity, entityType, onMessage }) {
  const vehicle = entity;
  // Un vehicule appartient a UNE filiale : sa devise vaut pour tout cet onglet.
  const { money: moneyOf } = useOrgCurrency();
  const money = (v) => moneyOf(v, vehicle);

  // Fetch insurance data
  const {
    etatDeParc,
    enrichedContracts,
    claims,
    claimsStats,
    loading,
    error,
    reload
  } = useVehicleInsuranceData(vehicle?.id, vehicle);

  // Accordion state - ALL CLOSED by default
  const accordion = useAccordionState({ defaultExpanded: [] });

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-600">Chargement des données d'assurance...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 bg-red-50 rounded-lg">
        <AlertTriangle className="w-12 h-12 text-red-300 mx-auto mb-2" />
        <p className="text-red-600">{error}</p>
        <button
          onClick={reload}
          className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
        >
          Réessayer
        </button>
      </div>
    );
  }

  const hasInsurance = enrichedContracts.length > 0;
  const hasClaims = claims.length > 0;

  if (!hasInsurance && !hasClaims) {
    return (
      <EmptyState
        icon={Shield}
        title="Aucune assurance"
        message="Ce véhicule n'est pas encore assuré et n'a aucun sinistre enregistré"
      />
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold text-gray-900">Assurance du Véhicule</h3>
        {claimsStats.open > 0 && (
          <span className="px-3 py-1 text-sm bg-red-100 text-red-800 rounded-full">
            {claimsStats.open} sinistre{claimsStats.open > 1 ? 's' : ''} en cours
          </span>
        )}
      </div>

      <AccordionContainer>
        {/* Contracts Section - First */}
        <AccordionGroup
          id="contracts"
          title="Contrats"
          count={enrichedContracts.length}
          icon={FileText}
          color="emerald"
          isExpanded={accordion.isExpanded('contracts')}
          onToggle={() => accordion.toggleGroup('contracts')}
          emptyMessage="Aucun contrat d'assurance"
        >
          <div className="space-y-3">
            {enrichedContracts.map((vi, index) => (
              <ContractCard key={vi.id || index} vi={vi} onMessage={onMessage} money={money} />
            ))}
          </div>
        </AccordionGroup>

        {/* Garanties Section - Second (formerly État de Parc) */}
        <AccordionGroup
          id="garanties"
          title="Garanties souscrites pour ce véhicule"
          icon={Car}
          color="blue"
          isExpanded={accordion.isExpanded('garanties')}
          onToggle={() => accordion.toggleGroup('garanties')}
          summary="Détail des garanties et couvertures"
        >
          <EtatDeParcCard data={etatDeParc} money={money} />
        </AccordionGroup>

        {/* Claims Section - Third */}
        <AccordionGroup
          id="claims"
          title="Sinistres"
          count={claims.length}
          icon={AlertTriangle}
          color="red"
          isExpanded={accordion.isExpanded('claims')}
          onToggle={() => accordion.toggleGroup('claims')}
          emptyMessage="Aucun sinistre enregistré"
          headerRight={
            claims.length > 0 && (
              <div className="flex items-center gap-2 text-sm">
                <span className="text-gray-500">
                  Total: <span className="font-semibold text-red-600">{money(claimsStats.totalEstimated)}</span>
                </span>
                {claimsStats.totalIndemnified > 0 && (
                  <span className="text-gray-500">
                    Remboursé: <span className="font-semibold text-green-600">{money(claimsStats.totalIndemnified)}</span>
                  </span>
                )}
              </div>
            )
          }
        >
          <div className="space-y-3">
            {claims.map(claim => (
              <ClaimCard key={claim.id} claim={claim} money={money} />
            ))}
          </div>
        </AccordionGroup>
      </AccordionContainer>
    </div>
  );
}

export default VehicleInsuranceTab;
